import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios'; // Used for API calls

// Import individual components
import Sidebar from './components/Sidebar';
import Dashboard from './components/Dashboard';
import UploadProducts from './components/UploadProducts';
import ReviewsSection from './components/ReviewsSection';
import ProductsSection from './components/ProductsSection';
import VendorManagement from './components/VendorManagement';
import Login from './components/Login'; // Import the Login component

function App() {
    // --- Global State Variables ---
    const [loading, setLoading] = useState(true); // General loading for data fetches
    const [message, setMessage] = useState(''); // General message display
    const [uploadedImages, setUploadedImages] = useState([]); // All images with their review stats
    const [aggregatedReviews, setAggregatedReviews] = useState([]); // Raw aggregated reviews from get_reviews.php
    const [vendors, setVendors] = useState([]); // List of vendors with their overall ratings

    // --- Authentication State ---
    const [isAuthenticated, setIsAuthenticated] = useState(false); // Controls if user is logged in
    const [loadingAuth, setLoadingAuth] = useState(true); // For initial authentication check on load

    // --- State for current view in the Admin Panel ---
    const [currentView, setCurrentView] = useState('dashboard'); // Controls which main content component is rendered

    // --- States for Upload Products section ---
    const [selectedVendorForUpload, setSelectedVendorForUpload] = useState('');

    // --- States for Vendor Management section ---
    const [newVendorName, setNewVendorName] = useState('');

    // --- States for Filtering and Sorting (shared by Reviews and Products sections) ---
    const [filterVendorName, setFilterVendorName] = useState(''); // Filter by vendor name string
    const [sortOrder, setSortOrder] = useState('none'); // 'none', 'avg_desc', 'avg_asc', 'reviews_desc'

    // Ref for the multiple file input element (used in UploadProducts component)
    const multipleFileInputRef = useRef(null);

    // IMPORTANT: Replace this with the actual URL to your PHP API folder on cPanel
    const API_BASE_URL = 'https://survey.nodeinnovations.com/api';

    // Define the full URL for the logo using API_BASE_URL
    const LOGO_URL = `${API_BASE_URL}/images/logo_feather_dhara.png`; // Assuming logo is in api/images/

    // API Endpoints (all data APIs are PUBLIC for now)
    const VENDOR_STATS_API_URL = `${API_BASE_URL}/get_vendors.php`;
    const ADD_VENDOR_API_URL = `${API_BASE_URL}/add_vendor.php`;
    const GET_IMAGES_API_URL = `${API_BASE_URL}/get_images.php`;
    const GET_REVIEWS_API_URL = `${API_BASE_URL}/get_reviews.php`; // Aggregated reviews
    const UPLOAD_IMAGE_API_URL = `${API_BASE_URL}/upload_image.php`;
    const LOGIN_API_URL = `${API_BASE_URL}/login.php`; // Login endpoint
    const LOGOUT_API_URL = `${API_BASE_URL}/logout.php`; // Logout endpoint
    const CHECK_SESSION_API_URL = `${API_BASE_URL}/check_session.php`; // Endpoint to check session status


    // --- Authentication Handlers ---
    const handleLoginSuccess = () => {
        console.log("handleLoginSuccess: Login successful, setting isAuthenticated to true.");
        setIsAuthenticated(true); // Set authenticated to true
        setMessage('Login successful!'); // Clear any previous login messages
        // fetchData() will be called by the useEffect dependent on isAuthenticated
    };

    const handleLogout = async () => {
        console.log("handleLogout: Initiating logout process.");
        try {
            // Call logout API to clear session cookie on backend
            const response = await fetch(LOGOUT_API_URL, {
                credentials: 'include', // Send session cookie to backend for clearing
            });
            const data = await response.json();
            console.log("Logout API response:", data.message);
        } catch (error) {
            console.error('Logout API error:', error);
        } finally {
            // Reset frontend authentication state
            setIsAuthenticated(false);
            setMessage('Logged out successfully.');
            // Optionally clear loaded data if it's sensitive (e.g., for security)
            setVendors([]);
            setUploadedImages([]);
            setAggregatedReviews([]);
            setCurrentView('dashboard'); // Reset view to dashboard
            console.log("handleLogout: Frontend authentication state reset.");
        }
    };

    // --- Initial Authentication Check on Component Mount ---
    useEffect(() => {
        const checkAuthStatus = async () => {
            console.log("checkAuthStatus: Checking initial authentication status...");
            try {
                const response = await fetch(CHECK_SESSION_API_URL, {
                    method: 'GET',
                    credentials: 'include',
                });
                const data = await response.json();
                console.log("checkAuthStatus: check_session.php response:", data);

                if (data.success) {
                    setIsAuthenticated(true);
                    setMessage('Session restored.');
                    console.log("checkAuthStatus: Session restored, isAuthenticated set to true.");
                } else {
                    setIsAuthenticated(false);
                    console.log("checkAuthStatus: Session NOT restored, isAuthenticated set to false.");
                }
            } catch (error) {
                console.error("checkAuthStatus: Initial auth check error:", error);
                setMessage('Could not verify session. Please log in.');
                setIsAuthenticated(false);
            } finally {
                setLoadingAuth(false); // Finished initial loading check
                console.log("checkAuthStatus: Finished initial auth check, loadingAuth set to false.");
            }
        };

        checkAuthStatus();
    }, []); // Runs once on component mount

    // --- Generic API Fetcher with Auth Handling ---
    const authenticatedFetch = async (url, options = {}) => {
        console.log(`authenticatedFetch: Attempting to fetch from ${url}`);
        try {
            const response = await fetch(url, {
                ...options,
                credentials: 'include', // Always send cookies
            });

            console.log(`authenticatedFetch: Response status for ${url}: ${response.status}`);

            // If response is 401 Unauthorized, force logout
            if (response.status === 401) {
                setMessage('Session expired or unauthorized. Please log in again.');
                console.error(`authenticatedFetch: 401 Unauthorized from ${url}. Forcing logout.`);
                handleLogout(); // Trigger logout on frontend
                return null; // Indicate failure
            }
            // If response is 403 Forbidden
            if (response.status === 403) {
                setMessage('Access denied. You do not have permission for this action.');
                console.error(`authenticatedFetch: 403 Forbidden from ${url}. Access denied.`);
                return null; // Indicate failure
            }

            // For successful responses (2xx), or other client errors (4xx) that are not 401/403
            const data = await response.json();
            if (!response.ok) { // Check for HTTP errors (e.g., 400, 500)
                setMessage(data.message || `API Error: ${response.statusText}`);
                console.error(`authenticatedFetch: API Error (${response.status}) from ${url}:`, data.message);
                return null;
            }
            console.log(`authenticatedFetch: Successfully fetched data from ${url}.`);
            return data; // Return parsed data for successful calls
        } catch (error) {
            console.error(`authenticatedFetch: Network or unexpected error during API call to ${url}:`, error);
            setMessage('Network error. Please check your internet connection or server status.');
            return null;
        }
    };


    // --- Data Fetching Function (Fetches all necessary data for the dashboard) ---
    const fetchData = async () => {
        console.log("fetchData: Starting data fetch for authenticated user...");
        setLoading(true);
        setMessage('');
        try {
            // Use the new authenticatedFetch for all protected API calls
            const imagesData = await authenticatedFetch(GET_IMAGES_API_URL);
            const aggregatedReviewsData = await authenticatedFetch(GET_REVIEWS_API_URL);
            const vendorsData = await authenticatedFetch(VENDOR_STATS_API_URL);

            if (!imagesData || !aggregatedReviewsData || !vendorsData) {
                console.log("fetchData: One or more data fetches returned null (likely auth error or network issue). Stopping data processing.");
                setLoading(false);
                return; // Stop here if any fetch failed (e.g., due to 401/403 handled by authenticatedFetch)
            }

            // Process Images Data
            if (imagesData.success && imagesData.images) {
                console.log("fetchData: Processing images data.");
                const reviewsMap = {};
                if (aggregatedReviewsData.success && aggregatedReviewsData.aggregatedReviews) {
                    aggregatedReviewsData.aggregatedReviews.forEach(review => {
                        reviewsMap[review.image_id] = review;
                    });
                    setAggregatedReviews(aggregatedReviewsData.aggregatedReviews);
                } else {
                    console.error("fetchData: Error fetching aggregated reviews:", aggregatedReviewsData.message || 'Unknown error');
                }

                const formattedImages = imagesData.images.map(img => {
                    const reviewStats = reviewsMap[img.unique_image_id] || {
                        good_reviews: 0,
                        average_reviews: 0,
                        bad_reviews: 0,
                        total_reviews: 0,
                        average_score: 0
                    };
                    return {
                        ...img,
                        imageUrl: `${API_BASE_URL}/${img.image_path}`,
                        goodReviews: parseInt(reviewStats.good_reviews, 10) || 0,
                        averageReviews: parseInt(reviewStats.average_reviews, 10) || 0,
                        badReviews: parseInt(reviewStats.bad_reviews, 10) || 0,
                        totalReviews: parseInt(reviewStats.total_reviews, 10) || 0,
                        averageScore: parseFloat(reviewStats.average_score).toFixed(2)
                    };
                });
                setUploadedImages(formattedImages);
            } else {
                setMessage(imagesData.message || 'Failed to fetch images.');
                setUploadedImages([]);
                console.error("fetchData: Failed to fetch images or imagesData.success is false.");
            }

            // Process Vendors Data
            if (vendorsData.success && vendorsData.vendors) {
                console.log("fetchData: Processing vendors data.");
                setVendors(vendorsData.vendors);
            } else {
                console.error("fetchData: Failed to fetch vendors:", vendorsData.message || 'Unknown error');
                setVendors([]);
            }

        } catch (error) {
            console.error('fetchData: Unexpected error during data fetch:', error);
            setMessage('An unexpected error occurred while fetching data.');
        } finally {
            setLoading(false);
            console.log("fetchData: Data fetch process completed.");
        }
    };

    // --- useEffect for initial data fetch (only after authentication is confirmed) ---
    useEffect(() => {
        console.log(`useEffect [isAuthenticated]: isAuthenticated is ${isAuthenticated}.`);
        if (isAuthenticated) {
            fetchData(); // Fetch data only if authenticated
            const interval = setInterval(fetchData, 30000); // Refresh every 30 seconds
            console.log("useEffect [isAuthenticated]: Data refresh interval set.");
            return () => {
                clearInterval(interval); // Cleanup on component unmount or isAuthenticated changes to false
                console.log("useEffect [isAuthenticated]: Data refresh interval cleared.");
            };
        } else {
            console.log("useEffect [isAuthenticated]: Not authenticated, not fetching data.");
        }
    }, [isAuthenticated]); // Dependency on isAuthenticated

    // --- Handler for Multiple Image Uploads (sends each file to upload_image.php) ---
    const handleMultipleImageUpload = async (event) => {
        const files = Array.from(event.target.files);
        if (files.length === 0) {
            setMessage('No files selected for upload.');
            return;
        }
        if (!selectedVendorForUpload) {
            setMessage('Please select a vendor before uploading.');
            return;
        }

        setLoading(true);
        setMessage(`Starting upload of ${files.length} images...`);
        let successfulUploads = 0;
        let failedUploads = 0;
        const uploadPromises = files.map(async (file) => {
            const formData = new FormData();
            formData.append('image', file);
            formData.append('vendorName', selectedVendorForUpload);

            const data = await authenticatedFetch(UPLOAD_IMAGE_API_URL, {
                method: 'POST',
                body: formData,
            });

            if (data && data.success) {
                successfulUploads++;
                console.log(`Upload successful for ${file.name}: ${data.message}`);
            } else {
                failedUploads++;
                console.error(`Upload failed for ${file.name}: ${data ? data.message : 'No response data'}`);
            }
            setMessage(`Uploaded ${successfulUploads + failedUploads}/${files.length} images. Success: ${successfulUploads}, Failed: ${failedUploads}`);
        });

        await Promise.all(uploadPromises);

        if (failedUploads === 0) {
            setMessage(`All ${successfulUploads} images uploaded successfully!`);
        } else if (successfulUploads === 0) {
            setMessage(`All ${failedUploads} uploads failed. Please check server logs.`);
        } else {
            setMessage(`Uploads finished: ${successfulUploads} successful, ${failedUploads} failed.`);
        }

        setLoading(false);
        fetchData(); // Refresh all data after uploads
        if (multipleFileInputRef.current) multipleFileInputRef.current.value = '';
    };

    // --- Handler for Adding New Vendor ---
    const handleAddVendor = async () => {
        if (!newVendorName.trim()) {
            setMessage('Vendor name cannot be empty.');
            return;
        }

        setLoading(true);
        setMessage('');

        const data = await authenticatedFetch(ADD_VENDOR_API_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ vendor_name: newVendorName }),
        });

        if (data && data.success) {
            setMessage(`Vendor added successfully: ${data.message}`);
            setNewVendorName(''); // Clear input field
            fetchData(); // Refresh vendor list and other data
        } else {
            setMessage(data ? data.message : 'Failed to add vendor.');
        }
        setLoading(false);
    };

    // --- Data Calculations for Dashboard KPIs ---
    const totalReviewsCount = aggregatedReviews.reduce((sum, img) => sum + img.total_reviews, 0);
    const totalProductsCount = uploadedImages.length;
    const totalVendorsCount = vendors.length;

    // --- Filtered and Sorted Images (for Dashboard, Reviews, Products sections) ---
    const filteredAndSortedImages = uploadedImages
        .filter(image => {
            if (filterVendorName && image.vendor_name) {
                return image.vendor_name.toLowerCase().includes(filterVendorName.toLowerCase());
            }
            return true;
        })
        .sort((a, b) => {
            if (sortOrder === 'avg_desc') {
                return b.averageScore - a.averageScore;
            } else if (sortOrder === 'avg_asc') {
                return a.averageScore - b.averageScore;
            } else if (sortOrder === 'reviews_desc') {
                return b.totalReviews - a.totalReviews;
            }
            return new Date(b.uploaded_at).getTime() - new Date(a.uploaded_at).getTime();
        });

    // --- Top Performing Images (for Dashboard) ---
    const topPerformingImages = uploadedImages
        .filter(image => parseFloat(image.averageScore) >= 4)
        .sort((a, b) => b.averageScore - a.averageScore)
        .slice(0, 10);


    // --- Main Conditional Rendering Logic ---

    // 1. Show Loading Authentication Status
    if (loadingAuth) {
        return (
            <div className="flex justify-center items-center h-screen text-gray-700 text-lg">
                Checking authentication status...
            </div>
        );
    }

    // 2. If not authenticated, show Login Screen
    if (!isAuthenticated) {
        return <Login onLoginSuccess={handleLoginSuccess} loginApiUrl={LOGIN_API_URL} />;
    }

    // 3. If authenticated, show Admin Panel
    return (
        <div className="flex min-h-screen bg-gray-100 font-sans">
            {/* Sidebar Navigation Component */}
            <Sidebar currentView={currentView} setCurrentView={setCurrentView} />

            {/* Main Content Area */}
            <div className="flex-1 p-8 overflow-y-auto">
                <div className="max-w-7xl mx-auto bg-white p-6 rounded-lg shadow-md">
                    {/* Logo and Admin Panel Title */}
                    <div className="flex items-center justify-center mb-6">
                        <img src={LOGO_URL} alt="Company Logo" className="h-10 mr-4" />
                        <h1 className="text-4xl font-bold text-[#4b433b]">ADMIN PANEL</h1>
                    </div>

                    <div className="flex justify-end mb-4">
                        <button
                            onClick={handleLogout}
                            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors duration-200"
                        >
                            Logout
                        </button>
                    </div>

                    {loading && <p className="text-center text-blue-500 mb-4">Loading admin data...</p>}
                    {message && (
                        <p className={`text-center mb-4 ${message.includes('failed') || message.includes('Error') || message.includes('required') || message.includes('denied') || message.includes('Authentication required') ? 'text-red-500' : 'text-green-500'}`}>
                            {message}
                        </p>
                    )}

                    {/* --- Conditional Rendering of Main Content Components --- */}
                    {currentView === 'dashboard' && (
                        <Dashboard
                            totalReviewsCount={totalReviewsCount}
                            totalProductsCount={totalProductsCount}
                            totalVendorsCount={totalVendorsCount}
                            topPerformingImages={topPerformingImages}
                            API_BASE_URL={API_BASE_URL}
                        />
                    )}

                    {currentView === 'upload' && (
                        <UploadProducts
                            selectedVendorForUpload={selectedVendorForUpload}
                            setSelectedVendorForUpload={setSelectedVendorForUpload}
                            vendors={vendors} // Pass vendors for the dropdown
                            handleMultipleImageUpload={handleMultipleImageUpload}
                            multipleFileInputRef={multipleFileInputRef} // Pass ref for file input
                        />
                    )}

                    {currentView === 'reviews' && (
                        <ReviewsSection
                            filteredAndSortedImages={filteredAndSortedImages}
                            filterVendorName={filterVendorName}
                            setFilterVendorName={setFilterVendorName}
                            sortOrder={sortOrder}
                            setSortOrder={setSortOrder}
                            API_BASE_URL={API_BASE_URL}
                        />
                    )}

                    {currentView === 'products' && (
                        <ProductsSection
                            filteredAndSortedImages={filteredAndSortedImages}
                            filterVendorName={filterVendorName}
                            setFilterVendorName={setFilterVendorName}
                            sortOrder={sortOrder}
                            setSortOrder={setSortOrder}
                            API_BASE_URL={API_BASE_URL}
                        />
                    )}

                    {currentView === 'vendors' && (
                        <VendorManagement
                            newVendorName={newVendorName}
                            setNewVendorName={setNewVendorName}
                            handleAddVendor={handleAddVendor}
                            vendors={vendors} // Pass vendors for the table
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

export default App;
